"use client";
import { useRouter } from "next/navigation";
import { ServiceCard } from "@/components/ServiceCard";
import { Button } from "@/components/ui/button";
import {
  ArrowRight,
  Sparkles,
  Leaf,
  Zap,
  Wind,
  Flame,
  Palette,
  Star,
  Heart,
} from "lucide-react";
import { SERVICES_DATA } from "@/lib/constants";

/**
 * Featured homepage treatments.
 * `slug` maps directly to the individual service page at /services/[slug].
 * Clicking a card takes the user straight to that page — no popup.
 */
const FEATURED_SERVICES = [
  {
    slug: "acne-treatment",
    title: "Acne Treatment",
    description:
      "Targeted solutions to clear skin and reduce breakouts effectively today.",
    icon: Leaf,
  },
  {
    slug: "laser-hair-removal",
    title: "Laser Hair Reduction Treatment",
    description:
      "Advanced laser technology for smooth, hair-free skin with lasting results.",
    icon: Zap,
  },
  {
    slug: "hairfall-treatment",
    title: "Hairfall Treatment",
    description:
      "Evidence-based solutions including PRP and mesotherapy to reduce hair loss.",
    icon: Wind,
  },
  {
    slug: "carbon-laser-facial",
    title: "Carbon Laser Facial",
    description:
      "Non-invasive laser facial that refreshes skin and refines pores instantly.",
    icon: Flame,
  },
  {
    slug: "pigmentation-treatment",
    title: "Pigmentations",
    description:
      "Brighten and even out your skin tone with safe, proven procedures.",
    icon: Palette,
  },
  {
    slug: "anti-aging-treatment",
    title: "Anti Aging Treatment",
    description:
      "Comprehensive solutions to combat signs of aging and maintain youth.",
    icon: Star,
  },
  {
    slug: "bridal-skincare",
    title: "Bridal Skincare",
    description:
      "Customized regimen for glowing, flawless skin on your special day.",
    icon: Heart,
  },
  {
    slug: "skin-boosters",
    title: "Skin Boosters/Stem Cells",
    description:
      "Bio-remodelling with hyaluronic acid for deep repair and volume.",
    icon: Sparkles,
  },
];

interface ServicesProps {
  onBookingClick: () => void;
  // Kept for prop-compatibility with existing callers (e.g. HomeClient),
  // though the 8 featured cards below now navigate directly to their
  // service page instead of invoking this callback.
  onLearnMore: (service: (typeof SERVICES_DATA)[0]) => void;
}

/**
 * Services Section Component
 * Displays a grid of featured treatments on the homepage. Clicking any
 * card navigates straight to that treatment's dedicated service page
 * (no popup/modal).
 */
export function Services({ onBookingClick }: ServicesProps) {
  const router = useRouter();

  return (
    <section id="services" className="py-8" aria-labelledby="services-title">
      <div className="container mx-auto px-4">
        {/* Section Header: Title and introductory text for the services grid */}
        <div className="mb-12 text-center">
          <div className="bg-golden/10 text-golden mb-3 inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-semibold">
            <Sparkles className="h-4 w-4" aria-hidden="true" />
            Our Services
          </div>
          <h2
            id="services-title"
            className="mb-4 text-3xl font-bold md:text-4xl"
          >
            Premium Treatments
          </h2>
          <p className="text-muted-foreground mx-auto max-w-lg text-lg">
            Experience world-class skin and hair treatments with cutting-edge
            technology and expert care.
          </p>
        </div>

        {/* Services Grid: each card links directly to its own service page */}
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {FEATURED_SERVICES.map((service, index) => (
            <ServiceCard
              key={service.slug}
              icon={service.icon}
              title={service.title}
              description={service.description}
              delay={index * 50}
              onLearnMore={() => router.push(`/services/${service.slug}`)}
            />
          ))}
        </div>

        {/* Bottom CTA: Encourages users to book after browsing services */}
        <div className="mt-12 flex flex-wrap items-center justify-center gap-4 text-center">
          <Button
            size="lg"
            variant="outline"
            asChild
            className="border-golden/40 text-golden hover:bg-golden/10"
          >
            <a href="https://www.skinsioclinics.com/services" aria-label="View all services">
              View All Treatments
              <ArrowRight className="ml-2 h-5 w-5" aria-hidden="true" />
            </a>
          </Button>
          <Button
            size="lg"
            onClick={onBookingClick}
            className="bg-gradient-to-r from-rose to-golden text-white shadow-lg shadow-rose/20 hover:opacity-90"
            aria-label="Book your treatment now"
          >
            Book Your Treatment
            <ArrowRight className="ml-2 h-5 w-5" aria-hidden="true" />
          </Button>
        </div>
      </div>
    </section>
  );
}
